import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

import {
  DeviceConfigProperty,
  DeviceHeatingCircuitIdCommands,
  DeviceRoles,
  Feature,
  FeatureNames,
  IsActiveProperty,
  IsNumberValueProperty,
  RoomCommands,
  RoomProperties,
  StandAloneProperty,
} from '@vi-iot/client';

import { GTMService } from '@services';
import {
  DevicesStore,
  GatewayStore,
  InstallationFeaturesHelpers,
  InstallationFeaturesStore,
  RemoteDeviceType,
  getTypeOfRemoteDevice,
} from '@store';

import { CommissioningService } from '../../../../commissioning/commissioning.service';
import { ManageRoomModalComponent } from '../../../manage-room-modal/manage-room-modal.component';
import { SelectChannelModalComponent } from '../../../select-channel-modal/select-channel-modal.component';
import { SelectHeatingCircuitModalComponent } from '../../../select-heating-circuit-modal/select-heating-circuit-modal.component';

@Component({
  selector: 'app-src-room',
  templateUrl: './src-room.component.html',
  styleUrls: ['./src-room.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrcRoomComponent implements OnInit, OnDestroy {
  manageRoomDialogRef: MatDialogRef<
    ManageRoomModalComponent,
    { deviceId?: string; heatingCircuitId?: number }
  >;
  heatingCircuitId$: {
    [deviceId: string]: Observable<Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>>;
  } = {};
  mountingMode$: Observable<Feature<IsActiveProperty, never>>;
  readonly srcType = RemoteDeviceType;
  readonly roomConstraints = InstallationFeaturesHelpers;

  @Input() room: Feature<RoomProperties, RoomCommands>;
  standaloneRoomFeature: Feature<StandAloneProperty, unknown>;
  deviceConfigurationFeature: Feature<DeviceConfigProperty, unknown>;
  private isSelectedDeviceOBNG = false;

  get roomId(): string {
    return this.room.feature.split('.')[1];
  }

  private ngUnsubscribe$ = new Subject<void>();
  constructor(
    public gatewayStore: GatewayStore,
    public gtmService: GTMService,
    public installationFeaturesStore: InstallationFeaturesStore,
    private matDialog: MatDialog,
    private commissioningService: CommissioningService,
    public devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    this.devicesStore.isSelectedDeviceOBNG$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isSelectedDeviceOBNG) => {
        this.isSelectedDeviceOBNG = isSelectedDeviceOBNG;
      });
    this.installationFeaturesStore
      .selectInstallationDeviceFeature(
        FeatureNames.standaloneMode,
        this.room.deviceId,
        this.room.gatewayId
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature: Feature<StandAloneProperty, unknown>) => {
        this.standaloneRoomFeature = feature;
      });
    this.installationFeaturesStore
      .selectInstallationDeviceFeature(
        FeatureNames.deviceConfiguration,
        this.room.deviceId,
        this.room.gatewayId
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature: Feature<DeviceConfigProperty, unknown>) => {
        this.deviceConfigurationFeature = feature;
      });

    this.devicesStore.isSelectedDeviceOBNG$
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe((isObng) => {
        if (isObng) {
          return;
        }
        this.room.properties.actors.value.forEach((actor) => {
          if (actor.deviceId) {
            this.mountingMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
              FeatureNames.etrvMountingMode,
              actor.deviceId
            );
            this.heatingCircuitId$[actor.deviceId] = this.gatewayStore.getHeatingCircuitOfDevice(
              actor.deviceId
            );
          }
        });
      });
  }

  areDevicesAssignedToRoom(room: Feature<RoomProperties, RoomCommands>): boolean {
    return !!room?.properties?.actors?.value?.length;
  }

  openManageRoomDialog(room: Feature<RoomProperties, RoomCommands>): void {
    this.manageRoomDialogRef = this.matDialog.open(ManageRoomModalComponent, {
      width: '715px',
      panelClass: 'commissioning-manage-room-dialog',
      data: room.feature,
      disableClose: true,
      autoFocus: false,
    });

    this.manageRoomDialogRef
      .afterClosed()
      .pipe(
        filter((data) => data && !!data.deviceId),
        switchMap((modalData) =>
          this.gatewayStore
            .getDeviceRoles(modalData.deviceId)
            .pipe(map((roles) => ({ modalData, roles })))
        ),
        take(1)
      )
      .subscribe(({ modalData, roles }) => {
        if (this.isSelectedDeviceOBNG) {
          const remoteDeviceType = getTypeOfRemoteDevice(roles);
          const needsChannelSelection =
            remoteDeviceType === RemoteDeviceType.TRV ||
            remoteDeviceType === RemoteDeviceType.VITOTROL_100E ||
            remoteDeviceType === RemoteDeviceType.VITOTROL_300E;

          if (needsChannelSelection) {
            // OBNG: Vitotrol/eTRV require the user to pick a channel (heating circuit).
            this.onAddDeviceToRoomWithChannel(modalData.deviceId);

            return;
          }

          // OBNG: other devices are assigned to the room directly, no channel step.
          this.commissioningService.assignDeviceToRoomStandalone(
            modalData.deviceId,
            this.room.feature,
            undefined,
            true
          );

          return;
        }

        const heatingCircuitAlreadyAssigned = modalData.heatingCircuitId != null;

        const isRoomNotNeededToChooseHeatingCircuit =
          roles.includes(DeviceRoles.ClimateSensor) || roles.includes(DeviceRoles.Co2Sensor);

        if (
          heatingCircuitAlreadyAssigned ||
          isRoomNotNeededToChooseHeatingCircuit ||
          this.standaloneRoomFeature?.properties?.active?.value ||
          !this.deviceConfigurationFeature?.properties?.circuitsEnabled?.value?.length
        ) {
          this.commissioningService.assignDeviceToRoomStandalone(
            modalData.deviceId,
            this.room.feature,
            /**
            heatingCircuitId is optional on some devices like Climate Sensor but BE required it,
            so we need to provide a default value
            */
            modalData.heatingCircuitId ?? 0
          );
        } else {
          this.onAddDeviceToRoomWithoutAssignedHc(modalData.deviceId);
        }
      });
  }

  onAddDeviceToRoomWithoutAssignedHc(deviceId: string): void {
    const dialog = this.matDialog.open(SelectHeatingCircuitModalComponent, {
      width: '715px',
      panelClass: 'commissioning-choose-hc-dialog',
      disableClose: true,
      autoFocus: false,
    });

    dialog.afterClosed().subscribe((modalData) => {
      if (modalData?.heatingCircuitId != null) {
        this.commissioningService.assignDeviceToRoomStandalone(
          deviceId,
          this.room.feature,
          modalData.heatingCircuitId
        );
      }
    });
  }

  onAddDeviceToRoomWithChannel(deviceId: string): void {
    const dialog = this.matDialog.open(SelectChannelModalComponent, {
      width: '715px',
      panelClass: 'commissioning-choose-hc-dialog',
      disableClose: true,
      autoFocus: false,
    });

    dialog.afterClosed().subscribe((modalData) => {
      if (modalData?.channel != null) {
        this.commissioningService.assignDeviceToRoomStandalone(
          deviceId,
          this.room.feature,
          modalData.channel,
          true
        );
      }
    });
  }

  onAddDevice(): void {
    this.commissioningService.openAddDeviceDialog(this.room.feature);
    this.gtmService.track('onConfirm', 'Add device or channel', 'room');
  }

  onEditRoom(): void {
    this.commissioningService.openEditRoomDialog(this.room);
  }

  onUnassignDeviceFromRoom(deviceId: string): void {
    this.commissioningService.unassignDeviceFromRoom(deviceId, this.room);
  }

  onDeleteRoom(): void {
    this.commissioningService.deleteRoom(this.room);
    this.gtmService.track('onConfirm', 'Delete room', 'room');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
